import React, { useCallback, useEffect, useRef, useState } from "react"
import { CollapsableContainer } from "@ciq/shared/components/collapsable"
import { EntryPillSelector, EntrySelector } from "@ciq/shared/components/modal"
import { Scrubber } from "@ciq/shared/components/scrubber"
import { useLeftPaneData } from "./useLeftPaneData"
import { ReactComponent as PlayIcon } from "@ciq/shared/assets/icons/play_icon.svg"
import { ReactComponent as PauseIcon } from "@ciq/shared/assets/icons/pause_icon.svg"
import { Animator } from "./animator"
import { useAnalyticsContext } from "@ciq/shared/contexts/AnalyticsContext"

const HOURS = Array.from({ length: 24 }).map((_, i) => i)
const HOUR_LOOKUP = HOURS.map(hour => {
  if (hour === 0) return "12am"
  if (hour < 12) return `${hour}am`
  if (hour === 12) return `${hour}pm`
  return `${hour - 12}pm`
})

/**
 * Dataset module for the left panel containing dataset selector and hourly controls.
 * Non-collapsible section with Dataset Type (Semester/Hourly) and conditional hourly playback controls.
 *
 * @returns {React.ReactElement} Dataset module component
 */
export function DatasetModule() {
  const { dataset } = useLeftPaneData()
  const { map } = useAnalyticsContext()

  // Convert dataset for EntryPillSelector format
  const datasetValues = dataset.dataset.options.map(opt => ({
    ...opt,
    selected: opt.value === dataset.dataset.current?.value,
  }))

  const handleDatasetChange = useCallback(
    selected => {
      dataset.dataset.onChange(selected)
      map.trackDatasetChanged({
        dataset: selected.value === "hourly" ? "hourly" : "semester",
      })
    },
    [dataset.dataset, map]
  )

  return (
    <CollapsableContainer className="dataset-module" collapsible={false} header="Dataset">
      <EntryPillSelector values={datasetValues} onChange={handleDatasetChange} />
      <HourlyControls show={dataset.dataset.isHourly} />
    </CollapsableContainer>
  )
}

/**
 * Hourly controls component containing days selector, playback controls, and hour slider.
 * Only visible when hourly dataset is selected.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the hourly controls
 * @returns {React.ReactElement|null} Hourly controls component or null if hidden
 */
function HourlyControls({ show }) {
  const { dataset } = useLeftPaneData()
  const { days, hour, playback } = dataset.hourlyControls

  const [isPlaying, setPlaying] = useState(false)

  const animatorRef = useRef(null)

  const { map } = useAnalyticsContext()

  const stopPlaying = useCallback(() => {
    if (isPlaying) {
      setPlaying(false)
    }

    if (animatorRef.current?.isRunning) {
      animatorRef.current.stop()
    }
  }, [isPlaying])

  // Initialize animator
  useEffect(() => {
    animatorRef.current = new Animator()
    return () => {
      animatorRef.current.destroy()
      animatorRef.current = null
    }
  }, [])

  // Stop animator when controls are hidden
  useEffect(() => {
    if (!show) {
      stopPlaying()
    }
  }, [show, stopPlaying])

  // Setup animator callback
  useEffect(() => {
    if (!show || !animatorRef.current) {
      return
    }

    animatorRef.current.setCallback(accumulatedTime => {
      const requiredTime = 1 / playback.speedModifier

      if (accumulatedTime < requiredTime) {
        return false
      }

      let newHour = hour.current + 1
      if (newHour >= 24) {
        newHour = 0
      }

      hour.setHour(newHour)
      return true
    })
  }, [show, hour, playback.speedModifier])

  const handlePlayToggle = useCallback(() => {
    const newPlaying = !isPlaying

    setPlaying(newPlaying)
    if (newPlaying) {
      animatorRef.current.start()
      map.trackPlaybackStarted({ speed: playback.speedModifier })
    } else {
      animatorRef.current.stop()
    }
  }, [isPlaying, playback.speedModifier, map])

  const handleSpeedChange = useCallback(
    selected => {
      const previousSpeed = playback.speedModifier
      playback.setPlaybackSpeed(selected.value)
      map.trackPlaybackSpeedChanged({
        speed: selected.value,
        previous_speed: previousSpeed,
      })
    },
    [playback, map]
  )

  if (!show) {
    return null
  }

  // Convert days for EntryPillSelector
  const dayValues = days.values

  // Prepare speed selector data
  const currentSpeed = playback.speedOptions.find(opt => opt.value === playback.speedModifier)
  const speedSelectorData = {
    value: currentSpeed,
    options: playback.speedOptions.filter(opt => opt.value !== playback.speedModifier),
    onChange: handleSpeedChange,
  }

  return (
    <div className="dataset-module-hourly-controls">
      {/* Days Section */}
      <div className="dataset-module-days">
        <EntryPillSelector
          label="Days"
          values={dayValues}
          isMulti={true}
          onChange={selectedDays => {
            days.onChange(selectedDays)
            map.trackDaysChanged({
              days: selectedDays.map(d => d.value),
              day_labels: selectedDays.map(d => d.label),
              day_count: selectedDays.length,
            })
          }}
        />
      </div>

      {/* Playback Controls Section */}
      <div className="dataset-module-playback-section">
        <div className="dataset-module-playback-controls">
          <button onClick={handlePlayToggle} className="da-icon-button">
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>
          <div className="dataset-module-speed-selector">
            <EntrySelector {...speedSelectorData} />
          </div>
          <span className="dataset-module-hour-label">{hour.label}</span>
        </div>

        {/* Hour Slider Section */}
        <div className="dataset-module-hour-slider">
          <Scrubber
            title=""
            interval={hour.current}
            intervals={HOURS}
            onChange={newHour => {
              hour.setHour(newHour)
              stopPlaying()
              map.trackHourChanged({
                hour: newHour,
                hour_label: HOUR_LOOKUP[newHour],
              })
            }}
          />
          <div className="dataset-module-range-labels">
            <span>12am</span>
            <span>11pm</span>
          </div>
        </div>
      </div>
    </div>
  )
}
